import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { useLocation, useSearchParams } from 'react-router';

import { SHOW_SELECTED_URL_PARAM } from '@/constants/constants';
import { useAppSelector } from '@/hooks/redux-hooks';
import { extractFiltersFromParams } from '@/pages/Catalog/utils/extractFiltersFromParams';
import { getEmptyFilterState } from '@/pages/Catalog/utils/getEmptyFilterState';

import { FilterContext } from './FilterContextBase';
import { FilterState } from '../../types/filter';

export const FilterProvider: FC<{
  children: ReactNode;
  disableSearchParams?: boolean;
}> = ({ children, disableSearchParams = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localFilters, setLocalFilters] = useState<FilterState>(() =>
    disableSearchParams ? getEmptyFilterState() : extractFiltersFromParams(searchParams)
  );

  useEffect(() => {
    if (!disableSearchParams) {
      setLocalFilters(extractFiltersFromParams(searchParams));
    }
  }, [searchParams, disableSearchParams]);

  const isHaveEmptySymbols = useAppSelector(state => state.symbolSelector.symbols.length === 0);

  const location = useLocation();

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(params => {
        const currentParams = Object.fromEntries(params.entries());

        for (const [key, value1] of Object.entries(updates)) {
          if (value1 === null || value1 === '') {
            delete currentParams[key];
          } else {
            currentParams[key] = value1;
          }
        }

        return currentParams;
      });
    },
    [setSearchParams]
  );

  const setFilter = useCallback(
    (key: string, value: string | null | boolean) => {
      const isToggling =
        key !== 'search' &&
        value !== null &&
        String(value) === String(localFilters[key as keyof FilterState]);

      const newValue = isToggling ? null : value;

      setLocalFilters(prev => ({
        ...prev,
        [key]: newValue
      }));

      if (!disableSearchParams) {
        updateSearchParams({
          [key]: newValue === null ? null : String(newValue)
        });
      }
    },
    [updateSearchParams, localFilters, disableSearchParams]
  );

  useEffect(() => {
    if (
      // Prevents redirecting to catalog after create DH
      (location.pathname.includes('catalog') || location.pathname.includes('dashboard')) &&
      isHaveEmptySymbols &&
      localFilters[SHOW_SELECTED_URL_PARAM]
    ) {
      setFilter(SHOW_SELECTED_URL_PARAM, null);
    }
  }, [isHaveEmptySymbols, localFilters, location.pathname, setFilter]);

  const clearAllFilters = useCallback(() => {
    setLocalFilters(getEmptyFilterState());
    if (!disableSearchParams) {
      setSearchParams({}, { replace: true });
    }
  }, [setSearchParams, disableSearchParams]);

  const isAnyFilterApplied = useMemo(() => {
    const { [SHOW_SELECTED_URL_PARAM]: showSelectedSymbols, search, ...rest } = localFilters;
    return !Object.values(rest).every(value => value === null);
  }, [localFilters]);

  const value = useMemo(
    () => ({
      filters: localFilters,
      clearAllFilters,
      setFilter,
      isAnyFilterApplied
    }),
    [localFilters, clearAllFilters, setFilter, isAnyFilterApplied]
  );

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
};
